/** Always returns a new Set, so `useMemo`/`useState` consumers see a changed identity. */
export function toggleInSet<T>(current: ReadonlySet<T>, value: T): ReadonlySet<T> {
  const next = new Set(current);
  if (!next.delete(value)) next.add(value);
  return next;
}

export function setEach<T>(
  current: ReadonlySet<T>,
  values: Iterable<T>,
  on: boolean
): ReadonlySet<T> {
  const next = new Set(current);
  for (const value of values) {
    if (on) next.add(value);
    else next.delete(value);
  }
  return next;
}

/**
 * Build a Map from an array, keyed by a property. Entries with null/undefined keys are skipped.
 * Replaces the common pattern: `new Map(items.filter(x => x.key).map(x => [x.key!, x]))`
 */
export function keyBy<T, K extends string | number>(
  items: T[],
  getKey: (item: T) => K | null | undefined
): Map<K, T> {
  const map = new Map<K, T>();
  for (const item of items) {
    const key = getKey(item);
    if (key != null) map.set(key, item);
  }
  return map;
}

/**
 * Build a Map from an array, mapping key → array index. Entries with null/undefined keys are skipped.
 * Replaces the common pattern: `new Map(items.map((x, i) => [x.key, i]))`
 */
export function indexBy<T, K extends string | number>(
  items: T[],
  getKey: (item: T) => K | null | undefined
): Map<K, number> {
  const map = new Map<K, number>();
  for (let i = 0; i < items.length; i++) {
    const key = getKey(items[i]);
    if (key != null) map.set(key, i);
  }
  return map;
}

export function mapOfSetsToRecord(
  map: ReadonlyMap<string, ReadonlySet<string>>
): Record<string, string[]> {
  const record: Record<string, string[]> = {};
  for (const [key, set] of map) record[key] = Array.from(set);
  return record;
}

/** Read a value from a nested object by a dotted path. Returns undefined if any segment is missing. */
export function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc != null && typeof acc === 'object') return (acc as Record<string, unknown>)[key];
    return undefined;
  }, obj);
}

export function setNestedValue(
  obj: Record<string, unknown>,
  path: (string | number)[],
  value: unknown
): void {
  let current: Record<string | number, unknown> = obj;
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];
    if (current[key] === null || current[key] === undefined) {
      current[key] = typeof path[i + 1] === 'number' ? [] : {};
    }
    current = current[key] as Record<string | number, unknown>;
  }
  current[path[path.length - 1]] = value;
}
